import { useEffect, useMemo, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Loader2, Wand2 } from "lucide-react";
import { fmtQty, GRADES, type StockForLineResponse, type StockUnitRecord } from "@/types/inventory";
import { Swatch } from "./StockStatusBadge";

export interface PickerLine {
  id: string;
  product_name: string;
  variant_name?: string | null;
  unit: string;
  /** What the line still needs: shown against the picked total */
  needed: number;
  /** The most the line may still take (with tolerance) */
  max: number;
}

interface Props {
  open: boolean;
  line: PickerLine | null;
  /** reserve: hold the rolls for the order now. pick: hand the chosen rolls back to the dispatch form. */
  mode: "reserve" | "pick";
  orderId?: string;
  /** pick mode: rolls already chosen on the form (otherwise those held for the line start ticked) */
  initialSelected?: string[];
  onClose: () => void;
  onReserved?: () => void;
  onPicked?: (units: StockUnitRecord[]) => void;
}

const r3 = (n: number) => Math.round((n + Number.EPSILON) * 1000) / 1000;

/**
 * Chooses rolls from stock for an order line, grouped by dye lot (oldest first). "Auto-pick" covers the needed
 * quantity from one lot when a single lot can, since rolls of one lot match in shade.
 */
export function StockPickerDialog({ open, line, mode, orderId, initialSelected, onClose, onReserved, onPicked }: Props) {
  const { success, error } = useToast();
  const [data, setData] = useState<StockForLineResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [heldHere, setHeldHere] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open || !line) return;
    setData(null);
    setLoading(true);
    api
      .get(`/api/SalesOrder/StockForLine/${line.id}`)
      .then((r) => {
        const d = unwrapObject<StockForLineResponse>(r);
        setData(d);
        const held = new Set(d.units.filter((u) => u.reserved_here).map((u) => u.id));
        setHeldHere(held);
        setSel(new Set(mode === "pick" && initialSelected?.length ? initialSelected : held));
      })
      .catch((err: any) => error("Could not load stock", err.message || "Please try again"))
      .finally(() => setLoading(false));
  }, [open, line?.id]);

  const byLot = useMemo(() => {
    const map = new Map<string, { lot_id: string; lot_number: string; units: StockUnitRecord[] }>();
    for (const u of data?.units || []) {
      const g = map.get(u.lot_id) || { lot_id: u.lot_id, lot_number: u.lot?.lot_number || "-", units: [] };
      g.units.push(u);
      map.set(u.lot_id, g);
    }
    return [...map.values()];
  }, [data]);

  const chosen = (data?.units || []).filter((u) => sel.has(u.id));
  const total = r3(chosen.reduce((s, u) => s + Number(u.quantity), 0));
  const lotsChosen = new Set(chosen.map((u) => u.lot_id)).size;
  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const setLot = (units: StockUnitRecord[], on: boolean) =>
    setSel((s) => {
      const n = new Set(s);
      for (const u of units) on ? n.add(u.id) : n.delete(u.id);
      return n;
    });

  const autoPick = () => {
    if (!line) return;
    const need = line.needed;
    const takeFrom = (units: StockUnitRecord[], limit: number) => {
      const picked: StockUnitRecord[] = [];
      let sum = 0;
      // Rolls already held for the line first, then the rest in roll order
      const order = [...units].sort((a, b) => Number(b.reserved_here) - Number(a.reserved_here));
      for (const u of order) {
        if (sum >= limit) break;
        picked.push(u);
        sum += Number(u.quantity);
      }
      return { picked, sum };
    };
    const single = byLot.find((g) => g.units.reduce((s, u) => s + Number(u.quantity), 0) >= need);
    const picked: StockUnitRecord[] = [];
    if (single) picked.push(...takeFrom(single.units, need).picked);
    else {
      let left = need;
      for (const g of byLot) {
        if (left <= 0) break;
        const t = takeFrom(g.units, left);
        picked.push(...t.picked);
        left -= t.sum;
      }
    }
    setSel(new Set(picked.map((u) => u.id)));
    if (!single && picked.length) error("No single lot is big enough", "The rolls come from more than one lot, so the shade may vary");
  };

  const confirm = async () => {
    if (!line) return;
    if (mode === "pick") {
      onPicked?.(chosen);
      onClose();
      return;
    }
    const toReserve = [...sel].filter((id) => !heldHere.has(id));
    const toRelease = [...heldHere].filter((id) => !sel.has(id));
    if (!toReserve.length && !toRelease.length) return onClose();
    setSaving(true);
    try {
      if (toRelease.length) await api.post(`/api/SalesOrder/ReleaseReserved/${orderId}`, { unit_ids: toRelease });
      let warnings: string[] = [];
      if (toReserve.length) {
        const r = unwrapObject<{ warnings?: string[] }>(await api.post(`/api/SalesOrder/Reserve/${orderId}`, { lines: [{ order_line_id: line.id, unit_ids: toReserve }] }));
        warnings = r.warnings || [];
      }
      success("Stock held for this order", warnings[0] || `${[...sel].length} roll${sel.size === 1 ? "" : "s"} · ${fmtQty(total, line.unit)}`);
      onReserved?.();
      onClose();
    } catch (err: any) {
      error("Could not hold the stock", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const over = line ? total > line.max + 0.0005 : false;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{mode === "reserve" ? "Hold stock for this order" : "Pick rolls from stock"}</DialogTitle>
          <DialogDescription>
            {line ? `${line.product_name}${line.variant_name ? ` · ${line.variant_name}` : ""} — needs ${fmtQty(line.needed, line.unit)}${line.max > line.needed ? ` (up to ${fmtQty(line.max, line.unit)})` : ""}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm overflow-y-auto max-h-[60vh] pr-1">
          {loading && (
            <div className="py-10 flex justify-center">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {data && (
            <>
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <p className="text-xs text-muted-foreground">
                  {data.units.length} roll{data.units.length === 1 ? "" : "s"} available in {byLot.length} lot{byLot.length === 1 ? "" : "s"}
                  {data.reserved_elsewhere_qty > 0 ? ` · ${fmtQty(data.reserved_elsewhere_qty, data.line.unit)} more is held for other orders` : ""}
                </p>
                <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" onClick={autoPick} disabled={!data.units.length}>
                  <Wand2 className="size-3.5" /> Auto-pick{line ? ` ${fmtQty(line.needed, line.unit)}` : ""}
                </Button>
              </div>
              {data.units.length === 0 && <p className="py-8 text-center text-muted-foreground">No stock of this product and shade is free right now.</p>}
              {byLot.map((g) => {
                const qty = r3(g.units.reduce((s, u) => s + Number(u.quantity), 0));
                const allOn = g.units.every((u) => sel.has(u.id));
                return (
                  <div key={g.lot_id} className="rounded-lg border border-border overflow-hidden">
                    <div className="flex items-center justify-between gap-2 bg-muted/40 px-3 py-2">
                      <div className="font-semibold">
                        Lot {g.lot_number} <span className="font-normal text-muted-foreground text-xs">· {g.units.length} rolls · {fmtQty(qty, line?.unit)}</span>
                      </div>
                      <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setLot(g.units, !allOn)}>
                        {allOn ? "Clear lot" : "Select lot"}
                      </Button>
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {g.units.map((u) => (
                          <tr key={u.id} className={`border-t border-border/60 ${sel.has(u.id) ? "bg-primary/5" : ""}`}>
                            <td className="px-3 py-1.5 w-8">
                              <input type="checkbox" checked={sel.has(u.id)} onChange={() => toggle(u.id)} aria-label={`Roll ${u.unit_no}`} />
                            </td>
                            <td className="px-2 py-1.5 font-mono font-semibold">{u.unit_no}</td>
                            <td className="px-2 py-1.5">
                              <Swatch hex={u.variant?.color_hex} />
                              {u.variant?.name || ""}
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground">{u.warehouse?.name}{u.location ? ` · ${u.location}` : ""}</td>
                            <td className="px-2 py-1.5 text-muted-foreground">{GRADES[u.grade] || u.grade}</td>
                            <td className="px-2 py-1.5 text-right font-semibold">{fmtQty(u.quantity, u.unit)}</td>
                            <td className="px-2 py-1.5 text-right text-amber-600">{u.reserved_here ? "Held here" : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </>
          )}
        </div>
        {data && (
          <div className="space-y-1 text-xs">
            <div className="flex items-center justify-between">
              <span>
                <b>{chosen.length}</b> roll{chosen.length === 1 ? "" : "s"} chosen · <b className={over ? "text-destructive" : ""}>{fmtQty(total, line?.unit)}</b>
                {line && ` of ${fmtQty(line.needed, line.unit)} needed`}
              </span>
              {over && <span className="text-destructive">More than the line can still take ({fmtQty(line!.max, line!.unit)})</span>}
            </div>
            {lotsChosen > 1 && (
              <p className="flex items-center gap-1.5 text-amber-600">
                <AlertTriangle className="size-3.5" /> These rolls come from {lotsChosen} lots, so the shade may vary between them.
              </p>
            )}
          </div>
        )}
        <DialogFooter className="pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={confirm} disabled={saving || loading || over || (mode === "pick" && !chosen.length)} className="gap-1.5">
            {saving && <Loader2 className="size-4 animate-spin" />}
            {mode === "reserve" ? "Hold these rolls" : `Use ${chosen.length} roll${chosen.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
